const PAN_THRESHOLD_PX = 3;

export function isWaveformScrollable(wavesurfer, wrapper) {
  if (!wavesurfer?.params?.scrollParent || !wrapper) {
    return false;
  }
  return wrapper.scrollWidth > wrapper.clientWidth + 1;
}

function isHandleTarget(target) {
  return Boolean(target.closest(".wavesurfer-handle"));
}

function isScrollbarArea(event, wrapper) {
  const scrollbarHeight = wrapper.offsetHeight - wrapper.clientHeight;
  if (scrollbarHeight <= 0) {
    return false;
  }
  const bbox = wrapper.getBoundingClientRect();
  return event.clientY >= bbox.bottom - scrollbarHeight;
}

function syncScrollableState(wavesurfer, wrapper, container, getRegion) {
  const scrollable = isWaveformScrollable(wavesurfer, wrapper);

  if (container) {
    container.classList.toggle("waveform-scrollable", scrollable);
  }

  wrapper.style.cursor = scrollable ? "grab" : "";

  const region = getRegion?.();
  if (region) {
    region.drag = !scrollable;
  }
}

export function attachWaveformPanScroll(wavesurfer, options = {}) {
  const wrapper = wavesurfer.drawer.wrapper;
  const { container = null, getRegion = null } = options;

  if (!wrapper) {
    return () => {};
  }

  let isPanning = false;
  let panStartX = 0;
  let panStartScrollLeft = 0;
  let didPan = false;
  let previousInteract = wavesurfer.params.interact;

  const updateScrollableState = () => {
    syncScrollableState(wavesurfer, wrapper, container, getRegion);
  };

  const beginPan = (clientX) => {
    isPanning = true;
    didPan = false;
    panStartX = clientX;
    panStartScrollLeft = wrapper.scrollLeft;
    wrapper.style.cursor = "grabbing";
  };

  const movePan = (clientX, preventDefault) => {
    if (!isPanning) return;

    const deltaX = clientX - panStartX;
    if (Math.abs(deltaX) <= PAN_THRESHOLD_PX) {
      return;
    }

    didPan = true;
    previousInteract = wavesurfer.params.interact;
    wavesurfer.params.interact = false;
    preventDefault?.();
    wrapper.scrollLeft = panStartScrollLeft - deltaX;
  };

  const endPan = () => {
    if (!isPanning) return;

    isPanning = false;
    updateScrollableState();

    if (!didPan) {
      return;
    }

    wavesurfer.params.interact = previousInteract;

    const suppressClick = (event) => {
      event.stopImmediatePropagation();
      event.preventDefault();
      wrapper.removeEventListener("click", suppressClick, true);
      document.removeEventListener("click", suppressClick, true);
    };
    wrapper.addEventListener("click", suppressClick, true);
    document.addEventListener("click", suppressClick, true);
  };

  const onMouseDownCapture = (event) => {
    if (event.button !== 0) return;
    if (!isWaveformScrollable(wavesurfer, wrapper)) return;
    if (isHandleTarget(event.target)) return;
    if (isScrollbarArea(event, wrapper)) return;

    beginPan(event.clientX);
    event.stopImmediatePropagation();
  };

  const onMouseMove = (event) => {
    movePan(event.clientX, () => event.preventDefault());
  };

  const onTouchStartCapture = (event) => {
    if (event.touches.length !== 1) return;
    if (!isWaveformScrollable(wavesurfer, wrapper)) return;
    if (isHandleTarget(event.target)) return;
    if (isScrollbarArea(event, wrapper)) return;

    beginPan(event.touches[0].clientX);
    event.stopImmediatePropagation();
  };

  const onTouchMove = (event) => {
    if (!isPanning || event.touches.length !== 1) return;
    movePan(event.touches[0].clientX, () => event.preventDefault());
  };

  const panContainer = container || wrapper;
  panContainer.addEventListener("mousedown", onMouseDownCapture, true);
  window.addEventListener("mousemove", onMouseMove);
  window.addEventListener("mouseup", endPan);
  panContainer.addEventListener("touchstart", onTouchStartCapture, {
    capture: true,
    passive: false,
  });
  window.addEventListener("touchmove", onTouchMove, { passive: false });
  window.addEventListener("touchend", endPan);
  wavesurfer.on("zoom", updateScrollableState);
  wavesurfer.on("redraw", updateScrollableState);
  updateScrollableState();

  return () => {
    panContainer.removeEventListener("mousedown", onMouseDownCapture, true);
    window.removeEventListener("mousemove", onMouseMove);
    window.removeEventListener("mouseup", endPan);
    panContainer.removeEventListener("touchstart", onTouchStartCapture, true);
    window.removeEventListener("touchmove", onTouchMove);
    window.removeEventListener("touchend", endPan);
    wavesurfer.un("zoom", updateScrollableState);
    wavesurfer.un("redraw", updateScrollableState);
    if (container) {
      container.classList.remove("waveform-scrollable");
    }
    wrapper.style.cursor = "";
    const region = getRegion?.();
    if (region) {
      region.drag = true;
    }
  };
}
